import type { ViewerActionParams, ViewerActionType } from "@situm/sdk-js";

import {
  resetSearch,
  setCurrentFloorID,
  unsetCurrentPoiID,
} from "@/features/cartography/cartographySlice";
import { setConfigItem } from "@/features/config/configSlice";
import {
  clearDirectionsRoutes,
  pushRoute,
  resetCurrentStep,
  setDirectionsErrors,
  setDirectionsSearchModeEnabled,
  setIsAutoPlaying,
  setRouteComputationStatus,
  setSelectedRouteId,
  setSelectedRouteType,
  updateCurrentStep,
  updateRouteTags,
} from "@/features/directions/directionsSlice";
import { AppUI, setTicketsSelected, setUIMode } from "../app/appSlice";
import { setFollowUser } from "../map/mapSlice";
import {
  resetNavigationEndpoints,
  setNavigationFrom,
  setNavigationTo,
} from "../navigation/navigationSlice";

import { findFlightById } from "@/features/flight_information/actions";
import type { WASMRoutesCalculator } from "@/features/native_routes/routeCalculatorWASM";
import store from "../../app/store";
import {
  type Building,
  CURRENT_USER_LOCATION_ID,
  FIND_MY_CAR_LOCATION_ID,
  type Floor,
  type Poi,
  type Pois,
  Position,
} from "../../libs/models";
import { assignedTags, type TicketType } from "../app/tickets/TicketsModal";
import { handleRemoveCurrentPoiCategories } from "../cartography/actions";
import { selectCurrentBuilding } from "../cartography/cartographySlice.selectors";
import { getNativeEndpoints } from "../native_routes/actions";
import type { IPartialRoute, WorkerResult } from "../native_routes/types";
import type {
  RouteIndication,
  RouteSegment,
  RouteStep,
} from "../navigation/types";
import { emmitEventRouteRequested } from "../utils/eventEmmiter";
import { areRoutesEquivalent } from "../utils/routes";
import { type FloorChange, FloorChangeType } from "../utils/types";
import {
  selectCurrentDirectionsRoute,
  selectDirectionsRoutes,
} from "./directionsSlice.selectors";
import { mapFindMyCarToPOI } from "./mapper";
import {
  DIRECTIONS_ERROR_CODES,
  type DirectionsRoute,
  RouteComputationStatus,
  RouteType,
} from "./types";

interface ComputeDirectionsProps {
  origin?: number | null;
  destination?: number | null;
  currentBuilding: Building | null | undefined;
  bearingFrom?: number | null;
  routesCalculator: WASMRoutesCalculator;
  routeType?: RouteType;
}

type SetDirectionsOptionsPayload = {
  includedTags?: string[];
  excludedTags?: string[];
};

/**
 * Parse an external navigation ID to a number. If the ID is not found
 * in the POIs of the building, try to parse it as a number in case it's
 * a special value, like "current_location" (-1) or "find_my_car" (-2).
 */
const parseExternalNavigationID = (id: string, pois?: Pois) => {
  try {
    return pois?.findByExternalId(id)?.id ?? Number(id);
  } catch (_) {
    console.error("Uknown external navigation ID", id);
    return null;
  }
};

/**
 * Maps a directions entry (flight id, external id, or POI id) to its Situm POI identifier.
 */
const getPoiIdFromDirectionsEntry = (
  poiId: number | null | undefined,
  externalId: string | null | undefined,
  flightId: string | null | undefined,
  pois: Pois | undefined,
): number | null | undefined => {
  if (typeof flightId === "string" && flightId.trim() !== "") {
    const flight = findFlightById(flightId);
    if (flight?.poiId == null) {
      console.error(
        `[flightNumber] Flight "${flightId}" has no associated POI.`,
      );
    }
    return flight?.poiId ?? null;
  }
  if (typeof externalId === "string" && externalId.trim() !== "") {
    return parseExternalNavigationID(externalId, pois);
  }
  if (poiId != null) {
    return Number(poiId);
  }
  return undefined;
};

/**
 * Callback to handle directions request
 */
export const handleDirectionsRequestFromAPI = (
  params: ViewerActionParams[ViewerActionType.DIRECTIONS_START],
) => {
  const uiMode = store.getState().app.uiMode;
  const { routeType } = params;
  if (
    uiMode == AppUI.navigation ||
    uiMode == AppUI.calibration ||
    (routeType != undefined && !Object.values(RouteType).includes(routeType))
  )
    return;

  const pois = selectCurrentBuilding(store.getState())?.pois;
  // Parameters of this function mark that only one `from` or `to` value can be provided.
  // For easier access, we cast the parameters to a generic record.
  const p = params as Record<string, unknown>;

  const fromPoiId = getPoiIdFromDirectionsEntry(
    p.navigationFrom as number | null | undefined,
    p.externalNavigationFrom as string | undefined,
    p.flightNavigationFrom as string | undefined,
    pois,
  );
  const toPoiId = getPoiIdFromDirectionsEntry(
    p.navigationTo as number | null | undefined,
    p.externalNavigationTo as string | undefined,
    p.flightNavigationTo as string | undefined,
    pois,
  );

  if (fromPoiId === undefined && toPoiId === undefined) {
    console.error(
      "Invalid payload for `DIRECTIONS_START`: provide an origin, a destination, or both",
      params,
    );
    return;
  }

  // clear current values
  handleRemoveCurrentPoiCategories();
  store.dispatch(unsetCurrentPoiID());

  // Set route type
  store.dispatch(setSelectedRouteType(routeType));

  // Set destination and origin POI IDs
  store.dispatch(setNavigationFrom(fromPoiId));
  store.dispatch(setNavigationTo(toPoiId));
};

/**
 * Prepare UI accordingly when the route computation has finished
 */
const onDirectionsComputationFinished = () => {
  const directions = store.getState().directions.routes;
  // If there is at least one route
  if (directions?.length != undefined && directions?.length > 0) {
    const selectedRouteType =
      store.getState().directions.selectedRouteType ??
      store.getState().config.directions.defaultRouteType;
    // Set selected route
    const selectedRoute =
      directions.find((r) => r.tags.includes(RouteType[selectedRouteType])) ||
      directions[0];
    store.dispatch(setSelectedRouteId(selectedRoute.id));
    // Travel to route's origin
    store.dispatch(
      setCurrentFloorID(parseInt(selectedRoute.segments[0].floorIdentifier)),
    );
    // Reset errors
    store.dispatch(setDirectionsErrors([]));
  } else {
    // If there is not route compute send error
    store.dispatch(
      setDirectionsErrors([DIRECTIONS_ERROR_CODES.untraceable_route]),
    );
  }
  if (
    store.getState().config.feature_flags.directions_autoplay_steps_timer !== 0
  ) {
    store.dispatch(setIsAutoPlaying(true));
  }
  store.dispatch(setRouteComputationStatus(RouteComputationStatus.FINISHED));
};

/**
 * Compute routes using WASM library
 */
const calculateNativeDirections = ({
  routesCalculator,
  origin,
  destination,
}: {
  routesCalculator: WASMRoutesCalculator;
  origin: number;
  destination: number;
  currentBuilding?: Building;
}) => {
  const { origin: routeOrigin, destination: routeDestination } =
    getNativeEndpoints(origin, destination);
  const availableRouteTypes = Object.values(RouteType);
  // Compute all available routes
  Promise.allSettled(
    Object.values(availableRouteTypes).map((routeType) => {
      return routesCalculator.calculateRoute(
        routeOrigin,
        routeDestination,
        routeType,
      );
    }),
  )
    .then((results) => {
      results
        .filter((r) => r.status == "fulfilled")
        .map(
          (r) =>
            (r as PromiseFulfilledResult<WorkerResult<IPartialRoute>>).value,
        )
        .map(({ payload: route }) => {
          if (route?.error) {
            console.warn(route.error);
            return;
          }
          const id = Object.values(RouteType).indexOf(route.type);
          if (route.segments.length > 0 && route.steps.length > 0) {
            onRouteCalculatedSuccessfully(
              origin,
              destination,
              route.segments,
              route.steps,
              route.indications,
              route.type,
              id,
            );
          }
        });
    })
    .finally(() => {
      onDirectionsComputationFinished();
    });
};

/**
 * Checks whether we are trying to recompute the same route
 */
const matchPreviousRoute = (
  origin: number,
  destination: number,
): DirectionsRoute | undefined => {
  if (origin == CURRENT_USER_LOCATION_ID) {
    store.dispatch(clearDirectionsRoutes());
    return;
  }
  const routes = store.getState().directions.routes;

  if (!routes) {
    return;
  }

  return routes.find(
    (r) => r.origin === origin && r.destination === destination,
  );
};

/**
 * Main entrypoint for route computation
 * @returns
 */
export const startComputingDirections = ({
  origin,
  destination,
  currentBuilding,
  routesCalculator,
}: ComputeDirectionsProps) => {
  const state = store.getState();
  const embeded = state.config.internal.embed;
  const routeComputationStatus = state.directions.routeComputationStatus;

  if (!currentBuilding || !currentBuilding?.paths) {
    console.error("no path in the building");
    return;
  }

  if (!origin || !destination) {
    return;
  }

  // Find any stored route with the same origin and destination
  const matchedRoute = matchPreviousRoute(origin, destination);
  if (matchedRoute) {
    store.dispatch(resetCurrentStep(matchedRoute.id));
    store.dispatch(setDirectionsErrors([]));
    return;
  }

  // Still computing routes
  if (
    embeded &&
    routeComputationStatus === RouteComputationStatus.CALCULATING
  ) {
    return;
  }

  // Initialization
  store.dispatch(clearDirectionsRoutes());

  if (origin == destination) {
    store.dispatch(
      setDirectionsErrors([DIRECTIONS_ERROR_CODES.origin_same_as_destination]),
    );
    return;
  } else {
    store.dispatch(setDirectionsErrors([]));
  }
  store.dispatch(setRouteComputationStatus(RouteComputationStatus.CALCULATING));

  emmitEventRouteRequested({
    destination: destination ?? CURRENT_USER_LOCATION_ID,
    location: {
      building_id: currentBuilding?.id,
      floor_id:
        state.location?.currentLocation?.floorId ??
        state.cartography.currentFloorID,
      lat: state.location?.currentLocation?.latitude ?? null,
      lng: state.location?.currentLocation?.longitude ?? null,
    },
    origin: origin ?? CURRENT_USER_LOCATION_ID,
    type: state.location?.currentLocation
      ? "location-based"
      : "location-independent",
  });

  calculateNativeDirections({
    currentBuilding,
    destination,
    origin,
    routesCalculator,
  });
};

export const processWasmSteps = (steps: RouteStep[]) => {
  const lastStep = steps[steps.length - 1];

  // If the lastStep is marked as isFirst and isLast then fix it.
  if (lastStep.isFirst) {
    lastStep.isFirst = false;
    steps[steps.length - 1] = lastStep;
  }

  return steps;
};

export const processWasmSegments = (segments: RouteSegment[]) => {
  return segments;
};

const onRouteCalculatedSuccessfully = async (
  sourceId: number,
  targetId: number,
  segments: RouteSegment[],
  steps: RouteStep[],
  indications: RouteIndication[],
  type: RouteType,
  id?: number,
) => {
  // Avoid extra unnecesary steps to be painted on map
  steps = processWasmSteps(steps);
  segments = processWasmSegments(segments);

  const route = {
    destination: targetId,
    id,
    indications,
    origin: sourceId,
    segments,
    steps,
    tags: [type],
  };
  const routes = store.getState().directions.routes || [];
  const equivalentRoute = routes.find((storedRoute) =>
    areRoutesEquivalent(storedRoute, route),
  );

  if (equivalentRoute?.tags.includes(type)) return;

  // If any route is equivalent, update it with the current route
  // tag and exit
  if (equivalentRoute) {
    store.dispatch(
      updateRouteTags({
        routeId: equivalentRoute.id,
        tags: route.tags,
      }),
    );
    return;
  }

  // Else store as a different route
  store.dispatch(pushRoute(route));
};

const filterValidElementsFromArray = (
  arrayToCheck: string[] | undefined,
  validArray: string[] | undefined,
) => {
  if (!arrayToCheck || !validArray) return null;
  return arrayToCheck.filter((e) => validArray.includes(e));
};

export const closeDirectionsSelector = () => {
  store.dispatch(setDirectionsSearchModeEnabled(false));
  store.dispatch(resetSearch());
};

export const clearDirectionsRouteState = () => {
  store.dispatch(unsetCurrentPoiID());
  store.dispatch(resetNavigationEndpoints());
  store.dispatch(clearDirectionsRoutes());
  store.dispatch(resetSearch());
};

export const onDirectionsCanceled = () => {
  store.dispatch(setDirectionsSearchModeEnabled(false));
  clearDirectionsRouteState();
  store.dispatch(setUIMode(AppUI.explore));
};

export const onDirectionsOptionsSet = (
  payload: SetDirectionsOptionsPayload,
) => {
  const currentBuildingID = store.getState().cartography.currentBuildingID;
  const currentBuildingTags = currentBuildingID
    ? store.getState().cartography.buildings[currentBuildingID].value.pathTags
    : [];

  const includedTags = filterValidElementsFromArray(
    payload.includedTags,
    currentBuildingTags,
  );
  const excludedTags = filterValidElementsFromArray(
    payload.excludedTags,
    currentBuildingTags,
  );

  includedTags &&
    store.dispatch(setConfigItem("directions.includedTags", includedTags));

  excludedTags &&
    store.dispatch(setConfigItem("directions.excludedTags", excludedTags));

  // FOR SAGP
  const tickets: TicketType[] =
    (includedTags &&
      includedTags.length > 0 &&
      assignedTags
        .filter((at) => includedTags.every((v) => at.tags.includes(v)))
        .map((t) => t.ticket)) ||
    [];

  tickets && store.dispatch(setTicketsSelected(tickets));
};

const mapToSDKEndpoint = (
  endpoint: Poi["id"] | Position,
  currentBuilding: Building,
) => {
  // Custom endpoint
  if (endpoint instanceof Position) {
    return endpoint.toPoi({ name: store.getState().navigation.navigationName });
  } else if (endpoint == CURRENT_USER_LOCATION_ID) {
    // Location
    return store.getState().location.currentLocation?.toPoi();
  } else if (endpoint == FIND_MY_CAR_LOCATION_ID) {
    // Find my car
    return mapFindMyCarToPOI(store.getState().findMyCar, currentBuilding);
  } else {
    // Poi
    return currentBuilding.pois?.getPoiById(endpoint);
  }
};

/**
 * Obtain origin and destionation POI for the routes
 * @returns
 */
export const getSDKEndpoints = ({
  origin,
  destination,
}: {
  origin?: Poi["id"] | null;
  destination?: Poi["id"] | Position | null;
}): {
  origin?: Poi | null;
  destination?: Poi | null;
} => {
  const currentBuilding = Object.values(store.getState().cartography.buildings)
    .map((b) => b.value)
    .find((b) => b.id == store.getState().cartography.currentBuildingID);
  return {
    destination: destination
      ? mapToSDKEndpoint(destination, currentBuilding)
      : null,
    origin: origin ? mapToSDKEndpoint(origin, currentBuilding) : null,
  };
};

/** Selects and a directions route by its route ID. */
export const handleDirectionsPathClick = (routeId: number) => {
  const state = store.getState();
  const directionsRoutes = selectDirectionsRoutes(state);
  const newDirectionsRoute = directionsRoutes?.find((r) => r.id === routeId);
  if (newDirectionsRoute) {
    store.dispatch(setSelectedRouteId(newDirectionsRoute.id));
  }
};

/** Updates the current directions step when a directions step is clicked. */
export const handleDirectionsStepClick = (indication: FloorChange) => {
  const state = store.getState();
  const currentBuilding = selectCurrentBuilding(state);
  const currentDirectionsRoute = selectCurrentDirectionsRoute(state);
  const { type, relatedLevel, stepId } = indication;

  const floor = currentBuilding?.floors?.find(
    (f: Floor) => f.level === relatedLevel && type === FloorChangeType.ORIGIN,
  );
  floor && store.dispatch(setCurrentFloorID(floor.id || null));
  if (currentDirectionsRoute) {
    const currentStep = currentDirectionsRoute.steps.find(
      (s) => s.id === stepId,
    );
    if (currentStep) {
      store.dispatch(setFollowUser(false));
      store.dispatch(
        updateCurrentStep({
          currentStep,
          routeId: currentDirectionsRoute.id,
        }),
      );
    }
  }
};
